import { memo, useContext, useEffect, useState } from 'react'
import { mdiCancel, mdiDeleteOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { sleep } from 'app/utils/sleep'
import { JobImageData } from 'features/jobs/types'
import { JobPageContext } from 'features/jobs/JobPageContext'
import { jobInfoImageFullSelector } from 'features/jobs/selectors'
import {
  deleteJobImageThunk,
  fetchJobImagesThunk,
  fetchJobImageThunk,
} from 'features/jobs/slice'
import { SpinnerCentered } from 'components/Loader'
import BaseModal from 'components/Modal/BaseModal'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { commonMessages } from 'app/i18n/messages'
import { RegularModerate } from 'components/Typography'
import { JobInfoImagesMessages } from '../messages'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  JobImage,
  JobImageBlockContainer,
  JobImageBottomBlock,
  JobImageDeleteButton,
  JobImageDeleteIcon,
  JobImageErrorIcon,
  JobImageModalContainer,
  JobImageSpinnerWrapper,
  JobModalImage,
} from './styles'
import { revokeObjectURL } from 'app/utils/createObjectURL'

interface JobImageBlockProps {
  jobImageThumb: JobImageData
}

function JobImageBlock({ jobImageThumb }: JobImageBlockProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { jobId } = useContext(JobPageContext)
  const jobImageFileSetId = jobImageThumb.metadata['file-set']
  const jobImageFullSize = useAppSelector(
    jobInfoImageFullSelector(jobImageFileSetId)
  )

  const [thumbImageUrl, setThumbImageUrl] = useState<string | null>()
  const [fullImageUrl, setFullImageUrl] = useState<string | null>()
  const [isError, setIsError] = useState<boolean>(false)
  const [isImageThumbLoading, setIsImageThumbLoading] = useState<boolean>(false)
  const [isFullSizeImageModalOpen, setIsFullSizeImageModalOpen] =
    useState(false)
  const [isDeleteImageModalOpen, setIsDeleteImageModalOpen] = useState(false)

  useEffect(() => {
    const getJobImageData = async () => {
      if (jobId && jobImageThumb.filename && jobImageFullSize?.filename) {
        try {
          setIsImageThumbLoading(true)
          const imageThumbUrl = await dispatch(
            fetchJobImageThunk({ jobId, fileName: jobImageThumb.filename })
          )
          setThumbImageUrl(imageThumbUrl)
          setIsImageThumbLoading(false)

          const imageFullSizeUrl = await dispatch(
            fetchJobImageThunk({ jobId, fileName: jobImageFullSize.filename })
          )
          setFullImageUrl(imageFullSizeUrl)
        } catch (err) {
          setIsError(err)
          setIsImageThumbLoading(false)
          console.log('Error getting job image', err)
        }
      }
    }
    getJobImageData()
  }, [dispatch, jobId, jobImageThumb.filename, jobImageFullSize?.filename])

  useEffect(() => {
    return () => {
      if (thumbImageUrl) {
        revokeObjectURL(thumbImageUrl)
      }
      if (fullImageUrl) {
        revokeObjectURL(fullImageUrl)
      }
    }
  }, [thumbImageUrl, fullImageUrl])

  const handleDeleteJobImage = async () => {
    if (jobId && jobImageThumb.filename && jobImageFullSize?.filename) {
      try {
        setIsDeleteImageModalOpen(false)
        setIsImageThumbLoading(true)
        await dispatch(
          deleteJobImageThunk({ jobId, fileName: jobImageThumb.filename })
        )
        await dispatch(
          deleteJobImageThunk({ jobId, fileName: jobImageFullSize.filename })
        )
        setThumbImageUrl(null)
        setFullImageUrl(null)
        // backend issue - it needs ~3 sec to update images data(info taken from old SCM)
        await sleep(3000)
        setIsImageThumbLoading(false)
        await dispatch(fetchJobImagesThunk(jobId))
      } catch (err) {
        console.log('Error deleting job image', err)
        setIsImageThumbLoading(false)
        setIsError(true)
      }
    }
  }

  return (
    <>
      <JobImageBlockContainer>
        {isImageThumbLoading && !isError && (
          <JobImageSpinnerWrapper>
            <SpinnerCentered />
          </JobImageSpinnerWrapper>
        )}
        {!isImageThumbLoading && (isError || !thumbImageUrl) && (
          <JobImageErrorIcon path={mdiCancel} />
        )}
        {!isImageThumbLoading && !isError && thumbImageUrl && (
          <JobImage
            src={thumbImageUrl}
            onClick={() => setIsFullSizeImageModalOpen(true)}
          />
        )}
        <JobImageBottomBlock>
          <IntlFormattedDate date={jobImageThumb.date_updated} withTime />
          <JobImageDeleteButton
            disabled={isError}
            onClick={() => setIsDeleteImageModalOpen(true)}
          >
            <JobImageDeleteIcon path={mdiDeleteOutline} />
          </JobImageDeleteButton>
        </JobImageBottomBlock>
      </JobImageBlockContainer>

      {isFullSizeImageModalOpen && !isError && (
        <BaseModal
          isOpen
          onRequestClose={() => setIsFullSizeImageModalOpen(false)}
        >
          <JobImageModalContainer>
            {fullImageUrl ? (
              <JobModalImage src={fullImageUrl} />
            ) : (
              <SpinnerCentered />
            )}
          </JobImageModalContainer>
        </BaseModal>
      )}

      {isDeleteImageModalOpen && !isError && (
        <ConfirmModal
          isOpen
          onDiscard={() => setIsDeleteImageModalOpen(false)}
          onConfirm={handleDeleteJobImage}
          title={formatMessage(JobInfoImagesMessages.deleteModalTitle)}
          discardBtnTitle={formatMessage(commonMessages.cancel)}
          confirmBtnTitle={formatMessage(commonMessages.delete)}
          variant="errorFilled"
        >
          <RegularModerate>
            {formatMessage(JobInfoImagesMessages.deleteModalMessage, {
              imageName: jobImageFullSize?.filename,
            })}
          </RegularModerate>
        </ConfirmModal>
      )}
    </>
  )
}

export default memo(JobImageBlock)
